import React from "react"
import {
  Box, BoxProps, Flex, Stack, Text, useToken,
} from "@chakra-ui/react"
import { Link as RouterLink } from "react-router-dom"
import Icon from "components/elements/Icon"

export type Segment = {
  label: string
  value: number
  color: string
}

export type LegendItem = Segment & { to?: string }

export const TICKETING_COLOR = "yellow.500"

const exactPercent = (value: number, total: number) => (total ? (value / total) * 100 : 0)
const percent = (value: number, total: number) => Math.round(exactPercent(value, total))

// Labels for rows that make up a whole add up to 100%: the leftover points go to the largest
// remainders. Only for display; bars use the exact share.
export const shares = (values: number[], total: number) => {
  if (!total || values.reduce((sum, value) => sum + value, 0) !== total) {
    return values.map((value) => percent(value, total))
  }

  const exact = values.map((value) => (value / total) * 100)
  const result = exact.map(Math.floor)
  const leftover = 100 - result.reduce((sum, value) => sum + value, 0)
  exact
    .map((value, index) => ({ index, remainder: value - result[index] }))
    .sort((a, b) => b.remainder - a.remainder)
    .slice(0, leftover)
    .forEach(({ index }) => { result[index] += 1 })

  return result
}

type TrackProps = BoxProps & { height?: number, track?: string }

const Track = ({
  height = 2.5, track = "gray.100", children, ...rest
}: TrackProps) => (
  <Box h={height} bg={track} borderRadius="full" overflow="hidden" {...rest}>{children}</Box>
)

type BarProps = { pct: number, color: string, height?: number, track?: string }

export const Bar = ({
  pct, color, height, track,
}: BarProps) => (
  <Track
    height={height}
    track={track}
    role="progressbar"
    aria-valuenow={pct}
    aria-valuemin={0}
    aria-valuemax={100}
  >
    <Box h="full" w={`${Math.min(100, Math.max(0, pct))}%`} bg={color} borderRadius="full" />
  </Track>
)

export const StackedBar = ({ items }: { items: Segment[] }) => {
  const total = items.reduce((sum, { value }) => sum + value, 0)

  return (
    <Track display="flex" gap="2px">
      {total > 0 && items.filter(({ value }) => value > 0).map(({ label, value, color }) => (
        <Box key={label} h="full" bg={color} flexBasis={`${(value / total) * 100}%`} flexShrink={0} />
      ))}
    </Track>
  )
}

type LegendProps = {
  items: LegendItem[]
  total?: number
  percentFirst?: boolean
  squares?: boolean
}

export const Legend = ({
  items, total, percentFirst, squares,
}: LegendProps) => {
  const sum = total ?? items.reduce((acc, { value }) => acc + value, 0)
  const itemShares = shares(items.map(({ value }) => value), sum)

  return (
    <Stack spacing={2}>
      {items.map(({
        label, value, color, to,
      }, index) => {
        const count = value.toLocaleString()
        const share = `${itemShares[index]}%`

        return (
          <Flex
            key={label}
            align="center"
            gap={2}
            {...to && {
              as: RouterLink, to, mx: -2, px: 2, py: 1, borderRadius: "md", _hover: { bg: "gray.50" }, "data-group": "",
            }}
          >
            <Box boxSize={2} borderRadius={squares ? "sm" : "full"} bg={color} flexShrink={0} />
            <Text flex={1} color="gray.700" noOfLines={1}>{label}</Text>
            <Text fontWeight="semibold" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {!percentFirst && count}
              <Text as="span" color="gray.600" fontWeight="normal">
                {percentFirst ? `${share} ` : ` · ${share}`}
              </Text>
              {percentFirst && count}
            </Text>
            {to && <Icon icon="next-arrow" size={2.5} color="gray.300" _groupHover={{ color: "gray.600" }} />}
          </Flex>
        )
      })}
    </Stack>
  )
}

export const ProgressRow = ({
  label, value, total, color, share = percent(value, total),
}: Segment & { total: number, share?: number }) => (
  <Box py={1.5}>
    <Flex justify="space-between" align="baseline" mb={1.5}>
      <Flex align="center" gap={2}>
        <Box boxSize={1.5} borderRadius="full" bg={color} flexShrink={0} />
        <Text color="gray.700">{label}</Text>
      </Flex>
      <Text fontWeight="semibold" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value.toLocaleString()}
        <Text as="span" color="gray.600" fontWeight="normal"> ({share}%)</Text>
      </Text>
    </Flex>
    <Bar pct={exactPercent(value, total)} color={color} />
  </Box>
)

export const ProgressRows = ({ rows, total }: { rows: Segment[], total: number }) => {
  const rowShares = shares(rows.map(({ value }) => value), total)

  return (
    <>
      {rows.map((row, index) => (
        <ProgressRow key={row.label} {...row} total={total} share={rowShares[index]} />
      ))}
    </>
  )
}

export const BigStat = ({ label, value }: { label: string, value: string | number }) => (
  <Flex justify="space-between" align="baseline" gap={3}>
    <Text fontWeight="semibold" color="gray.700">{label}</Text>
    <Text fontSize="2xl" fontWeight="semibold" sx={{ fontVariantNumeric: "tabular-nums" }}>
      {typeof value === "number" ? value.toLocaleString() : value}
    </Text>
  </Flex>
)

type DonutProps = {
  items: Segment[]
  size?: number
  stroke?: number
  centerValue: string
  centerLabel: string
}

export const Donut = ({
  items, size = 148, stroke = 18, centerValue, centerLabel,
}: DonutProps) => {
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const total = items.reduce((sum, { value }) => sum + value, 0)
  // SVG strokes need real colours, not theme token names.
  const [track, ...strokes] = useToken("colors", ["gray.100", ...items.map(({ color }) => color)])
  let offset = 0

  return (
    <Box position="relative" boxSize={`${size}px`} flexShrink={0}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={track} strokeWidth={stroke} />
        {total > 0 && items.map(({ label, value }, index) => {
          const length = (value / total) * circumference
          const dash = (
            <circle
              key={label}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={strokes[index]}
              strokeWidth={stroke}
              strokeDasharray={`${length} ${circumference - length}`}
              strokeDashoffset={-offset}
            />
          )
          offset += length

          return dash
        })}
      </svg>
      <Stack position="absolute" inset={0} align="center" justify="center" spacing={0}>
        <Text fontSize="2xl" fontWeight="semibold" lineHeight="short">{centerValue}</Text>
        <Text fontSize="xs" color="gray.600">{centerLabel}</Text>
      </Stack>
    </Box>
  )
}
